// src/pages/user/ProfilePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import FormInput from '../../components/resume/FormInput';

const API = process.env.REACT_APP_API_URL;

const ProfilePage = () => {
    const { user, isPremium, premiumSource, premiumUntil, cancelAtPeriodEnd, refreshEntitlements } = useAuth();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    const fetchProfile = useCallback(async () => {
        setIsFetching(true);
        try {
            const response = await fetch(`${API}/api/get_profile.php`, {
                method: 'GET', credentials: 'include', headers: { 'Accept': 'application/json' },
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setFirstName(result.first_name || '');
                setLastName(result.last_name || '');
                setPhoneNumber(result.phone_number || '');
                setDateOfBirth(result.date_of_birth || '');
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to load profile.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An error occurred while loading your profile.' });
        } finally {
            setIsFetching(false);
        }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);
    useEffect(() => { if (refreshEntitlements) refreshEntitlements(); }, [refreshEntitlements]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setIsLoading(true);
        try {
            const response = await fetch(`${API}/api/update_profile.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    first_name: firstName, last_name: lastName,
                    phone_number: phoneNumber, date_of_birth: dateOfBirth || null,
                }),
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setMessage({ type: 'success', text: 'Profile updated.' });
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to update profile.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An error occurred. Please try again later.' });
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const datePart = String(dateStr).split(/[ T]/)[0];
        const d = new Date(datePart + 'T00:00:00Z');
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    if (isFetching) {
        return (
            <div className="flex flex-col items-center justify-center py-32 text-neutral-400">
                <div className="h-8 w-8 rounded-full border-2 border-neutral-700 border-t-cyan-400 animate-spin mb-4" />
                Loading profile…
            </div>
        );
    }

    const isSubscription = premiumSource === 'subscription';
    const isAnnual = premiumSource === 'annual';
    const isStudent = premiumSource === 'student';
    const cardClass = 'rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 sm:p-7';

    return (
        // Spacing/centering is owned by SettingsLayout's pane — a fixed width
        // here keeps content from re-centering when switching settings tabs.
        <div className="text-white">
            <div className="max-w-2xl">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
                    <p className="text-sm text-neutral-400 mt-1">Manage your account, membership, and details.</p>
                </header>

                {/* Membership card */}
                <section className={`${cardClass} mb-6 relative overflow-hidden`}>
                    {isPremium && (
                        <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-cyan-500/20 blur-3xl" />
                    )}
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Membership</h2>
                        </div>

                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${isPremium ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/40' : 'bg-white/5 text-neutral-300 ring-1 ring-white/10'}`}>
                                    {isPremium ? '★ Premium' : 'Free'}
                                </span>
                                <div className="mt-2 text-sm text-neutral-300">
                                    {!isPremium && (
                                        <p className="text-neutral-400">5 AI optimizations/day · download locked</p>
                                    )}
                                    {isPremium && isAnnual && (
                                        <p>Annual pass — Premium until <span className="text-white font-medium">{formatDate(premiumUntil)}</span>. Won't auto-renew.</p>
                                    )}
                                    {isPremium && isStudent && (
                                        <p>Student trial — Premium until <span className="text-white font-medium">{formatDate(premiumUntil)}</span>.</p>
                                    )}
                                    {isPremium && isSubscription && !cancelAtPeriodEnd && (
                                        <p>Renews on <span className="text-white font-medium">{formatDate(premiumUntil)}</span>.</p>
                                    )}
                                    {isPremium && isSubscription && cancelAtPeriodEnd && (
                                        <p className="text-amber-300">Cancels — Premium until <span className="font-medium">{formatDate(premiumUntil)}</span>.</p>
                                    )}
                                </div>
                            </div>

                            {!isPremium && (
                                <Link to="/upgrade" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg transition-colors shadow-lg shadow-cyan-500/20">
                                    Upgrade
                                </Link>
                            )}
                        </div>

                        {/* Premium actions */}
                        {isPremium && (
                            <div className="mt-5 pt-5 border-t border-white/10">
                                <Link to="/settings/billing"
                                    className="inline-flex text-sm font-medium text-neutral-200 bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg transition-colors">
                                    Manage billing
                                </Link>
                            </div>
                        )}
                    </div>
                </section>

                {/* Personal info card */}
                <section className={cardClass}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Personal Information</h2>
                        </div>
                        <Link to="/settings/change-password" className="text-xs font-medium text-cyan-300 hover:text-cyan-200">
                            Change password
                        </Link>
                    </div>
                    {user?.email && (
                        <p className="text-sm text-neutral-500 mb-5">Signed in as <span className="text-neutral-300">{user.email}</span></p>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <FormInput label="First Name" type="text" name="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
                            <FormInput label="Last Name" type="text" name="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <FormInput label="Phone Number" type="tel" name="phoneNumber" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} autoComplete="tel" />
                            <div className="flex flex-col space-y-1">
                                <label htmlFor="dateOfBirth" className="text-sm font-medium text-neutral-300">Date of Birth</label>
                                <input id="dateOfBirth" type="date" name="dateOfBirth" value={dateOfBirth}
                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]} autoComplete="bday"
                                    className="bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent" />
                                <p className="text-[11px] text-neutral-500">Used for birthday offers — optional.</p>
                            </div>
                        </div>

                        {message.text && (
                            <p className={`text-sm ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message.text}</p>
                        )}

                        <div className="flex justify-end pt-1">
                            <button type="submit" disabled={isLoading}
                                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2.5 px-6 rounded-lg disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/10">
                                {isLoading ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default ProfilePage;
